import React from 'react';
import styled from 'styled-components';
import { useFormContext, Controller, ControllerRenderProps, ControllerFieldState, UseFormStateReturn } from 'react-hook-form';
import { Input, Select } from '../../common';
import PriceTypeSelector from '../../common/PriceTypeSelector';
import { PresetCreateRequest, LayeredOrderSettings } from '../../../types/preset';
import { LimitPriceType, PriceReference } from '../../../types/preset';

interface AdvancedOptionsProps {
  // Keep any essential props not directly related to form fields, if any
}

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #4b5563;
  margin: 0 0 0.5rem 0;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const InfoText = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0.25rem 0 0 0;
`;

const ErrorText = styled.p`
  color: #dc2626;
  font-size: 0.75rem;
  margin: 0.25rem 0 0 0;
`;

type ControllerRenderParams = {
  field: ControllerRenderProps<PresetCreateRequest, any>; 
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<PresetCreateRequest>; 
};

const AdvancedOptions: React.FC<AdvancedOptionsProps> = () => {
  const { control, watch, formState: { errors } } = useFormContext<PresetCreateRequest>();

  const layeredSettings = watch('layered');
  const layeredParts = layeredSettings?.parts || 2;
  const layeredStep = layeredSettings?.step || 1;
  const distributionType = layeredSettings?.distribution_type || 'step_based';
  const priceType = layeredSettings?.price_type || LimitPriceType.ABSOLUTE;
  
  // Distribution type options
  const distributionTypeOptions = [
    { value: 'step_based', label: 'Step from Reference Price' },
    { value: 'zone_based', label: 'Distribute in Zone' }
  ];
  
  // Distribution strategy options for zone-based orders
  const distributionStrategyOptions = [
    { value: 'even', label: 'Even Spacing' },
    { value: 'weighted', label: 'Weighted to Center' },
    { value: 'edge_focused', label: 'Edge Focused' }
  ];

  return (
    <StepContainer>
      <FormSection>
        <SectionTitle>Layered Order Settings</SectionTitle>
        <InfoText>
           Configure how your total order amount will be split into smaller limit orders.
        </InfoText>
        
        {/* Distribution Type Selector */}
        <InputGroup>
          <Label htmlFor="layered.distribution_type">Distribution Type</Label>
          <Controller
            name="layered.distribution_type"
            control={control}
            defaultValue="step_based"
            render={({ field }: ControllerRenderParams) => (
              <Select
                id="layered.distribution_type"
                options={distributionTypeOptions}
                {...field}
                value={field.value || 'step_based'}
              />
            )}
          />
        </InputGroup>

        {/* Price Type Selector - Same as limit orders */}
        {distributionType === 'step_based' && (
          <Controller
            name="layered.price_type"
            control={control}
            defaultValue={LimitPriceType.ABSOLUTE}
            render={({ field: priceTypeField }: ControllerRenderParams) => (
              <Controller
                name="layered.price_offset"
                control={control}
                render={({ field: priceOffsetField }: ControllerRenderParams) => (
                  <Controller
                    name="layered.price_reference"
                    control={control}
                    render={({ field: priceReferenceField }: ControllerRenderParams) => (
                      <PriceTypeSelector
                        priceType={priceTypeField.value || LimitPriceType.ABSOLUTE}
                        onPriceTypeChange={priceTypeField.onChange}
                        priceOffset={priceOffsetField.value}
                        onPriceOffsetChange={priceOffsetField.onChange}
                        priceReference={priceReferenceField.value || PriceReference.LAST}
                        onPriceReferenceChange={priceReferenceField.onChange}
                        label="Reference Price Type"
                        tooltipContent="Configure how the starting price for layered orders is determined. Same options as individual limit orders."
                      />
                    )}
                  />
                )}
              />
            )}
          />
        )}
        
        <FormRow>
          <InputGroup>
            <Label htmlFor="layered.parts">Number of Orders</Label>
            <Controller
              name="layered.parts"
              control={control}
              defaultValue={2}
              rules={{ 
                  required: 'Number of orders is required', 
                  min: { value: 2, message: 'Minimum 2 orders' },
                  max: { value: 50, message: 'Maximum 50 orders' } 
              }}
              render={({ field }: ControllerRenderParams) => (
                <Input
                  id="layered.parts"
                  type="number"
                  min="2"
                  max="50"
                  placeholder="e.g., 5 (2-50)"
                  {...field}
                  onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 2)}
                  value={field.value || 2}
                />
              )}
            />
             {errors.layered?.parts && <ErrorText>{errors.layered.parts.message}</ErrorText>}
          </InputGroup>
          
          {distributionType === 'step_based' && (
            <InputGroup>
              <Label htmlFor="layered.step">Price Step ($)</Label>
              <Controller
                name="layered.step"
                control={control}
                defaultValue={1}
                rules={{ 
                    required: 'Price step is required', 
                    min: { value: 0.01, message: 'Minimum step is 0.01' }
                }}
                render={({ field }: ControllerRenderParams) => (
                  <Input
                    id="layered.step"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="e.g., 10"
                    {...field}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 1)}
                    value={field.value || 1}
                  />
                )}
              />
               {errors.layered?.step && <ErrorText>{errors.layered.step.message}</ErrorText>}
            </InputGroup>
          )}
        </FormRow>

        {/* Zone-based distribution options */}
        {distributionType === 'zone_based' && (
          <>
            <InputGroup>
              <Label htmlFor="layered.distribution_strategy">Distribution Strategy</Label>
              <Controller
                name="layered.distribution_strategy"
                control={control}
                defaultValue="even"
                render={({ field }: ControllerRenderParams) => (
                  <Select
                    id="layered.distribution_strategy"
                    options={distributionStrategyOptions}
                    {...field}
                    value={field.value || 'even'}
                  />
                )}
              />
            </InputGroup>
            <InfoText>
              Zone boundaries (S2Line → S1Line) will be provided by webhook alerts. Orders will be distributed using the selected strategy.
            </InfoText>
          </>
        )}
        
        {/* Step-based distribution info */}
        {distributionType === 'step_based' && (
          <InfoText>
            Your total order size will be divided into {layeredParts} orders, each placed {layeredStep}$ apart from the reference price.
          </InfoText>
        )}
      </FormSection>
    </StepContainer>
  );
};

export default AdvancedOptions; 